import React from 'react'
import styled, { DefaultTheme, useTheme } from 'styled-components'
import { AlertCircle, CheckCircle, Info, Slash } from 'react-feather'
import { Text, TextVariant } from '../Text'
import { Flex } from '../Flex'
import { AlertProps, variants } from './types'
import { Button, ButtonVariant } from '../Button'
import { CloseIcon } from '../Svg'

interface ThemedIconLabel {
  variant: AlertProps['variant']
  theme: DefaultTheme
  hasDescription: boolean
}

const getThemeColor = ({ theme, variant = variants.INFO }: ThemedIconLabel) => {
  switch (variant) {
    case variants.DANGER:
      return theme.colors.red
    case variants.WARNING:
      return theme.colors.yellow
    case variants.SUCCESS:
      return theme.colors.green
    case variants.INFO:
      return theme.colors.purple

    default:
      return theme.colors.white3
  }
}

const getIcon = (variant: AlertProps['variant'] = variants.INFO) => {
  switch (variant) {
    case variants.DANGER:
      return Slash
    case variants.WARNING:
      return AlertCircle
    case variants.SUCCESS:
      return CheckCircle
    case variants.INFO:
    default:
      return Info
  }
}

const IconLabel = styled(Flex)<ThemedIconLabel>`
  color: ${getThemeColor};
  align-items: center;
`

const TextWrapper = styled(Flex)<{ hasHandler: boolean }>`
  width: 100%;
  flex-direction: column;
  justify-content: center;
`

const CloseWrapper = styled(Flex)`
  cursor: pointer;
`

const StyledAlert = styled(Flex)`
  position: relative;
  width: 100%;
  gap: 16px;
  padding: 8px;
  min-height: 64px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
  @-moz-document url-prefix() {
    background: ${({ theme }) => theme.colors.gray10} !important;
  }
`

const SideColor = styled(Flex)<ThemedIconLabel>`
  align-self: strech;
  width: 3px;
  border-radius: 8px;
  background-color: ${getThemeColor};
`

const Alert: React.FC<AlertProps> = ({ title, children, variant, onClick, buttonText, onButtonClick }) => {
  const Icon = getIcon(variant)
  const theme = useTheme()
  return (
    <StyledAlert>
      <SideColor variant={variant} hasDescription={!!children} />
      <IconLabel variant={variant} hasDescription={!!children}>
        <Icon color="currentColor" />
      </IconLabel>
      <TextWrapper hasHandler={!!onClick}>
        {React.isValidElement(title) ? title : <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{title}</Text>}
        {typeof children === 'string' ? <Text color={theme.colors.text}>{children}</Text> : children}
      </TextWrapper>
      <Flex alignItems="center">
        {buttonText && onButtonClick && (
          <Button variant={ButtonVariant.OUTLINED} size="sm" onClick={onButtonClick}>
            {buttonText}
          </Button>
        )}
      </Flex>
      {onClick && (
        <CloseWrapper>
          <CloseIcon width="20px" color={theme.colors.white} onClick={onClick} />
        </CloseWrapper>
      )}
    </StyledAlert>
  )
}

export default Alert
